import { STORE_MAX_UPDATE_FREQUENCY } from '../config';

/**
 * Little helper class that wraps caching from the store. We don't want to be running our store
 * fetches for things like properties for every single component individually, however we also want
 * the components to work on their own, regardless of what queries the rest of the page runs. This
 * means that we want to run things like `this.$store.dispatch(FETCH_FILTERS);` without
 * worrying about running 100 HTTP requests.
 *
 * This cache should be used for very short term storage, which means that while a page like the
 * dashboard is loading only 1 query will be run for filters, properties, etc.
 *
 * @class
 */
export default class StoreCache {
  /**
   * Creates the new cache object, each cache is an individual object to keep timers separate
   *
   * @constructor
   * @param {Object} args Extra arguments for the cache, such as "lifespan" for the duration
   */
  constructor(args) {
    this.pendingPromise = null;
    this.lastRunTime = 0;

    if (args && args.lifespan) {
      this.lifespan = args.lifespan;
    } else {
      this.lifespan = STORE_MAX_UPDATE_FREQUENCY;
    }
  }

  /**
   * Returns true if the cache data is fresh, false if it needs fetching
   *
   * @returns {Boolean}
   */
  isReady() {
    return (this.lastRunTime + this.lifespan) > Date.now();
  }

  /**
   * Returns true if there is a request currently being run
   *
   * @returns {Boolean}
   */
  isPending() {
    return this.pendingPromise !== null;
  }

  /**
   * Returns the request that is currently being run
   *
   * @returns {Promise}
   */
  getPendingPromise() {
    return this.pendingPromise;
  }

  /**
   * Set the promise to be tracked by the cache. Appends a function to the end of the promise, which
   * clears all of the cache object's pending data and updates the lastRunTime property
   *
   * @param {Promise} promise The request to be tracked by the cache
   * @returns Promise
   */
  setPendingPromise(promise) {
    this.pendingPromise = promise;

    // Clear itself once done
    this.pendingPromise.then(() => {
      this.lastRunTime = Date.now();
      this.pendingPromise = null;
    });

    return this.pendingPromise;
  }

  /**
   * Clear all data from the cache. The next request that gets run will not hit the cache
   */
  flush() {
    this.lastRunTime = 0;
    this.pendingPromise = null;
  }
}
